/**
 * Build-handoff modal. Opened by "Send to build": runs the design → build
 * package export with a cancellable "Preparing…" stage (live progress pushed
 * from the main process), then shows the generated phase plan so the user can
 * see what they're about to build before hitting "Start building" (which
 * dispatches the build directly through the existing interview:start-building
 * path).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignBuildPlanSeed, DesignChangeSummary } from '@volley/crucible-ipc-types';
import { MarkdownContent } from '../../components/MarkdownContent';
import { DesignModal } from './DesignModal';
import { cancelExport, commitBuildSnapshot, exportFoundryPackage } from './anvil-api';
import { PhasePlanSchema, type PhasePlan } from './schemas';

export interface BuildHandoffDialogProps {
  gameId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when the user confirms a send — lets the host refresh the "Send
   *  Build Updates" button state (the snapshot just moved). */
  onSent?: () => void;
  /** Proceed with a FULL build through the existing build-game state machine. */
  onStartBuild: (seededPlan?: DesignBuildPlanSeed) => Promise<void> | void;
  /** Proceed with an UPDATE — the host switches to Chat and auto-sends the
   *  edit list to the agent. */
  onSendUpdate?: (message: string) => void;
}

type Stage = 'preparing' | 'review' | 'error';

export function BuildHandoffDialog({
  gameId,
  open,
  onOpenChange,
  onSent,
  onStartBuild,
  onSendUpdate,
}: BuildHandoffDialogProps): ReactElement {
  const [stage, setStage] = useState<Stage>('preparing');
  const [message, setMessage] = useState('Preparing…');
  const [phases, setPhases] = useState<PhasePlan['phases']>([]);
  const [mode, setMode] = useState<'full' | 'update'>('full');
  const [planMarkdown, setPlanMarkdown] = useState('');
  const [subdir, setSubdir] = useState('');
  const [seededPlan, setSeededPlan] = useState<DesignBuildPlanSeed | undefined>(undefined);
  const [changeSummary, setChangeSummary] = useState<DesignChangeSummary | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cancelledRef = useRef(false);
  const runRef = useRef(0);

  // Kick off the export each time the modal opens.
  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    const myRun = (runRef.current += 1);
    setStage('preparing');
    setMessage('Preparing…');
    setError(null);
    setPhases([]);
    setMode('full');
    setPlanMarkdown('');
    setSubdir('');
    setSeededPlan(undefined);
    setChangeSummary(undefined);
    setConfirming(false);

    const live = (): boolean => !cancelledRef.current && runRef.current === myRun;
    const off = window.crucible.on('design:export:progress', (p) => {
      if (live()) setMessage(p.message);
    });

    exportFoundryPackage(gameId)
      .then((res) => {
        if (!live()) return;
        // Export only PREPARES the package — it does not move the snapshot, so
        // the button must NOT change yet. The commit happens on confirm.
        setMode(res.mode);
        setPlanMarkdown(res.planMarkdown);
        setSubdir(res.subdir);
        setSeededPlan(res.seededPlan);
        setChangeSummary(res.changeSummary);
        const parsed = PhasePlanSchema.safeParse(res.phasePlan);
        setPhases(parsed.success ? parsed.data.phases : []);
        setStage('review');
      })
      .catch((e: unknown) => {
        if (!live()) return;
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes('export-cancelled')) {
          onOpenChange(false);
          return;
        }
        setError(msg);
        setStage('error');
      })
      .finally(off);

    return off;
  }, [open, gameId]);

  const cancel = (): void => {
    cancelledRef.current = true;
    void cancelExport(gameId);
    onOpenChange(false);
  };

  // The user actually confirms the send (final button). ONLY here do we commit
  // the snapshot (advancing the "last sent" baseline) and refresh the host's
  // button — dismissing without confirming leaves both untouched.
  //
  // Full first build → start-building through build-game state. Update → Chat
  // tab, auto-sent as an edit list to the agent.
  const confirmSend = (): void => {
    if (confirming) return;
    setConfirming(true);
    setError(null);
    void (async () => {
      try {
        await commitBuildSnapshot(gameId, subdir);
        onSent?.();
        if (mode === 'update') {
          onOpenChange(false);
          onSendUpdate?.(planMarkdown);
        } else {
          await onStartBuild(seededPlan);
          onOpenChange(false);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setConfirming(false);
      }
    })();
  };

  // Closing (✕ / Escape) while preparing should also cancel the export.
  const handleOpenChange = (next: boolean): void => {
    if (!next && stage === 'preparing') {
      cancel();
      return;
    }
    onOpenChange(next);
  };

  return (
    <DesignModal
      open={open}
      onOpenChange={handleOpenChange}
      title="Send to build"
      dismissible={false}
    >
      {stage === 'preparing' && (
        <div
          className="flex flex-col items-center py-6 text-center"
          data-testid="build-handoff-preparing"
        >
          <div
            aria-label="Generating build plan"
            className="mb-4 h-7 w-7 animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent"
            data-testid="build-handoff-preparing-spinner"
            role="status"
          />
          <div className="mb-3 text-sm text-crucible-fg">{message}</div>
          <div className="mb-4 text-xs text-crucible-muted">
            Generating the build plan, screens, and agent hand-off from your design.
          </div>
          <button
            type="button"
            onClick={cancel}
            className="rounded border border-crucible-border px-3 py-1.5 text-sm text-crucible-muted hover:bg-crucible-bg-elevated"
          >
            Cancel
          </button>
        </div>
      )}

      {stage === 'error' && (
        <div data-testid="build-handoff-error">
          <p className="mb-3 rounded border border-red-500/40 p-2 text-xs text-red-400">{error}</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded border border-crucible-border px-3 py-1.5 text-sm text-crucible-muted hover:bg-crucible-bg-elevated"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {stage === 'review' && mode === 'update' && (
        <div data-testid="build-handoff-review">
          <p className="mb-3 text-sm text-crucible-muted">
            These are the design changes since your last build. The agent will apply them to the
            existing game — review, then send the updates.
          </p>
          {changeSummary && <ChangeSummaryHeader summary={changeSummary} />}
          <div className="max-h-72 overflow-y-auto rounded border border-crucible-border p-2 text-xs">
            <MarkdownContent content={planMarkdown || '_No changes detected._'} />
          </div>
          {error && (
            <p className="mt-3 rounded border border-red-500/40 p-2 text-xs text-red-400">
              {error}
            </p>
          )}
          {reviewButtons('Send design changes')}
        </div>
      )}

      {stage === 'review' && mode === 'full' && (
        <div data-testid="build-handoff-review">
          <p className="mb-3 text-sm text-crucible-muted">
            Here's the plan the build agent will follow. Review it, then start building.
          </p>
          {phases.length === 0 ? (
            <p className="text-sm text-crucible-muted">
              (No phases were returned — you can still start; the build agent will read the full
              design package.)
            </p>
          ) : (
            <ol className="space-y-3">
              {phases.map((ph, i) => (
                <li key={ph.id || i} className="rounded border border-crucible-border p-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-crucible-fg">
                      {i + 1}. {ph.title}
                    </span>
                    <span className="flex items-center gap-2 text-[11px] text-crucible-muted">
                      {ph.estimate && <span>{ph.estimate}</span>}
                      {ph.pauseForReview && (
                        <span className="rounded bg-amber-400/20 px-1.5 py-0.5 text-amber-400">
                          review checkpoint
                        </span>
                      )}
                    </span>
                  </div>
                  {ph.goal && <p className="mt-0.5 text-xs text-crucible-muted">{ph.goal}</p>}
                  {ph.deliverables.length > 0 && (
                    <ul className="mt-1 list-disc pl-5 text-xs text-crucible-fg">
                      {ph.deliverables.map((d, j) => (
                        <li key={j}>{d}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
          {error && (
            <p className="mt-3 rounded border border-red-500/40 p-2 text-xs text-red-400">
              {error}
            </p>
          )}
          {reviewButtons('Start building')}
        </div>
      )}
    </DesignModal>
  );

  /** Close + proceed buttons, shared by the full + update review stages. */
  function reviewButtons(startLabel: string): ReactElement {
    return (
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded border border-crucible-border px-3 py-1.5 text-sm text-crucible-muted hover:bg-crucible-bg-elevated"
        >
          Close
        </button>
        <button
          type="button"
          onClick={confirmSend}
          disabled={confirming}
          data-testid="build-handoff-start"
          className="rounded bg-crucible-accent px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {confirming ? 'Working…' : startLabel}
        </button>
      </div>
    );
  }
}

/** Compact counts header for the update review (added/removed/changed). */
function ChangeSummaryHeader({ summary }: { summary: DesignChangeSummary }): ReactElement {
  const s = summary.sections;
  const sc = summary.screens;
  const chips: string[] = [];
  const add = (label: string, n: number): void => {
    if (n > 0) chips.push(`${n} ${label}`);
  };
  add('section(s) changed', s.changed.length);
  add('section(s) added', s.added.length);
  add('section(s) removed', s.removed.length);
  add('screen(s) changed', sc.changed.length);
  add('screen(s) added', sc.added.length);
  add('screen(s) removed', sc.removed.length);
  if (summary.graphChanged) chips.push('dependency graph changed');
  return (
    <p className="mb-2 text-xs text-crucible-muted" data-testid="build-handoff-change-summary">
      {chips.length > 0 ? chips.join(' · ') : 'No changes detected.'}
    </p>
  );
}
